import { Suspense, lazy, useEffect } from 'react'
import { Route, Routes } from 'react-router-dom'

import s from 'App.module.scss'
import 'bootstrap/dist/css/bootstrap.min.css'

import { UnityUnloadContextProvider } from 'contexts/UnityUnloadContext'


import RequireAuth from 'components/helpers/RequireAuth/RequireAuth'
import LoginModal from 'components/modals/LoginModal'

import Audience from 'pages/Audience'
import AudienceInfoModal from 'pages/Audience/components/AudienceInfoModal'
import { CHeatmap } from 'pages/CHeatmap/CHeatmap'
import { Home } from 'pages/Home/Home'
import MainLayout from 'pages/MainLayout'
import ModerationPage from 'pages/Moderation'
import MyAdSpots from 'pages/MyAdSpots'
import AdSpotInfoModal from 'pages/MyAdSpots/components/AdSpotInfoModal'
import { CreateAdSpotModal } from 'pages/MyAdSpots/components/CreateAdSpotModal'
import Created from 'pages/MyAdSpots/components/CreateAdSpotModal/components/Created/Created'
import { EditAdSpotModal } from 'pages/MyAdSpots/components/EditAdSpot'
import MyCampaigns from 'pages/MyCampaigns'
import { CampaignDetailedInfo } from 'pages/MyCampaigns/components/CampaignDetailedInfo'
import CampaignInfoPopup from 'pages/MyCampaigns/components/CampaignInfoPopup'
import MyCreatives from 'pages/MyCreatives'
import { CreateCreativeModal } from 'pages/MyCreatives/components/CreateCreativeModal/index'
import CreateStreamModal from 'pages/MyCreatives/components/CreateSteamModal'
import CreativeInfoPopup from 'pages/MyCreatives/components/CreativeInfoModal'
import CreativeInfoModal from 'pages/MyCreatives/components/CreativeInfoModal'
import EditCreativeModal from 'pages/MyCreatives/components/EditCreative'
import { MyScenes } from 'pages/MyScenes/MyScenes'
import { EditSceneModal } from 'pages/MyScenes/components/EditSceneModal/EditSceneModal'
import { SceneInfoModal } from 'pages/MyScenes/components/SceneInfoModal/SceneInfoModal'
import NotFound from 'pages/NotFound'
import Settings from 'pages/Settings/Settings'
import { Spinner } from 'components/UI/Spinner/Spinner'

const AdvertiserDashboard = lazy(() => import('pages/AdvertiserDashboard'))
const PublisherDashboard = lazy(() => import('pages/PublisherDashboard'))
const CreateCampaign = lazy(() => import('pages/CreateCampaign'))
const CreateTemplate = lazy(() => import('pages/CreateAudience'))

const INTERCOM_APP_ID = process?.env?.REACT_APP_INTERCOM_APP_ID || ''

function App() {
  useEffect(() => {
    window.Intercom('boot', {
      api_base: 'https://api-iam.intercom.io',
      app_id: INTERCOM_APP_ID,
      hide_default_launcher: false,
    })
  }, [])

  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route path="login" element={<LoginModal />} />
        <Route
          path="settings"
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />
        <Route path="marketplace" element={<Home />}>
          <Route path=":id" element={<AdSpotInfoModal />} />
        </Route>
        {/*==========Publisher===========*/}
        <Route
          path="publisher/my-adspaces"
          element={
            <RequireAuth>
              <MyAdSpots />
            </RequireAuth>
          }
        >
          <Route path="create" element={<CreateAdSpotModal />} />
          <Route path=":id" element={<AdSpotInfoModal />} />
          <Route path="created/:pin" element={<Created />} />
          <Route path="edit/:id" element={<EditAdSpotModal />} />
          <Route
            path="heatmap/:id"
            element={
              <RequireAuth>
                <UnityUnloadContextProvider>
                  <CHeatmap heatmapFor="adspace" />
                </UnityUnloadContextProvider>
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path="publisher/moderation"
          element={
            <RequireAuth>
              <ModerationPage />
            </RequireAuth>
          }
        >
          <Route path="adspace/:id" element={<AdSpotInfoModal />} />
        </Route>
        <Route
          path="publisher/dashboard"
          element={
            <RequireAuth>
              <Suspense
                fallback={
                  <div className={s.loaderWrapper}>
                    <Spinner />
                  </div>
                }
              >
                <PublisherDashboard />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="publisher/scenes"
          element={
            <RequireAuth>
              <MyScenes />
            </RequireAuth>
          }
        >
          <Route path="edit/:id" element={<EditSceneModal />} />
          <Route path=":id" element={<SceneInfoModal />} />
          <Route
            path="heatmap/:id"
            element={
              <RequireAuth>
                <UnityUnloadContextProvider>
                  <CHeatmap heatmapFor="scene" />
                </UnityUnloadContextProvider>
              </RequireAuth>
            }
          />
        </Route>
        {/*==========Advertiser===========*/}
        <Route
          path="advertiser/dashboard"
          element={
            <RequireAuth>
              <Suspense
                fallback={
                  <div className={s.loaderWrapper}>
                    <Spinner />
                  </div>
                }
              >
                <AdvertiserDashboard />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="advertiser/my-campaigns"
          element={
            <RequireAuth>
              <MyCampaigns />
            </RequireAuth>
          }
        >
          <Route
            path=":id"
            element={
              <RequireAuth>
                <CampaignInfoPopup />
              </RequireAuth>
            }
          />
          <Route
            path="heatmap/:id"
            element={
              <RequireAuth>
                <UnityUnloadContextProvider>
                  <CHeatmap heatmapFor="campaign" />
                </UnityUnloadContextProvider>
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path="advertiser/my-campaigns/edit/:id"
          element={
            <RequireAuth>
              <Suspense
                fallback={
                  <div className={s.loaderWrapper}>
                    <Spinner />
                  </div>
                }
              >
                <CreateCampaign />
              </Suspense>
            </RequireAuth>
          }
        >
          <Route path="adspace/:id" element={<AdSpotInfoModal />} />
        </Route>
        <Route
          path="advertiser/my-campaigns/:id/info"
          element={
            <RequireAuth>
              <CampaignDetailedInfo />
            </RequireAuth>
          }
        />
        <Route
          path="advertiser/create-campaign"
          element={
            <RequireAuth>
              <Suspense
                fallback={
                  <div className={s.loaderWrapper}>
                    <Spinner />
                  </div>
                }
              >
                <CreateCampaign />
              </Suspense>
            </RequireAuth>
          }
        >
          <Route path="adspace/:id" element={<AdSpotInfoModal />} />
          <Route path="creative/:id" element={<CreativeInfoModal />} />
          <Route path="audience/:id" element={<AudienceInfoModal />} />
        </Route>
        <Route
          path="advertiser/my-creatives"
          element={
            <RequireAuth>
              <MyCreatives />
            </RequireAuth>
          }
        >
          <Route path=":id" element={<CreativeInfoPopup />} />
          <Route path="create" element={<CreateCreativeModal />} />
          <Route path="edit/:id" element={<EditCreativeModal />} />
          <Route path="create-stream" element={<CreateStreamModal />} />
        </Route>
        <Route
          path="advertiser/audience"
          element={
            <RequireAuth>
              <Audience />
            </RequireAuth>
          }
        >
          <Route path=":id" element={<AudienceInfoModal />}></Route>
        </Route>
        <Route
          path="advertiser/audience/create"
          element={
            <RequireAuth>
              <Suspense
                fallback={
                  <div className={s.loaderWrapper}>
                    <Spinner />
                  </div>
                }
              >
                <CreateTemplate />
              </Suspense>
            </RequireAuth>
          }
        >
          <Route path="creative/:id" element={<CreativeInfoModal />} />
        </Route>
        <Route
          path="advertiser/audience/edit/:id"
          element={
            <RequireAuth>
              <Suspense
                fallback={
                  <div className={s.loaderWrapper}>
                    <Spinner />
                  </div>
                }
              >
                <CreateTemplate />
              </Suspense>
            </RequireAuth>
          }
        >
          <Route path="creative/:id" element={<CreativeInfoModal />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default App
